import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './config/theme/globalStyles.css';
import 'bootstrap-daterangepicker/daterangepicker.css';

import Header from './common/Header';
import Routes from './routes';
import AppConstants from './constants';

import { AppState } from './config/redux/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { resetUserError, fetchUserAccessControl } from './Login/redux/loginSlice';
import { clearDeletedRoute,clearBulkDeletedRoute, clearUpdatedRoute, resetCreateRouteSuccess, resetPlanningError, clearPublishedRoutesErrors, clearPublishedRoutes, resetDraftRoute } from './RoutePlanning/redux/planningSlice';
import { resetUsersError, resetUsersResetPwd, setLoggedInUser } from './Users/redux/usersSlice';
import { UACProvider } from './contexts/UACContext';

interface AppProps {
  location?: any;
  history: any;
}

const App = (props: AppProps) => {
  const { history } = props;
  const {
    isLoggedIn,
    error: userLoginError,
    role,
    uacData,
    uacError,
  } = useSelector((state: AppState) => state.userLogin);

  const { error: planningOrderListError, routeCreateSuccess, updatedRoute, deletedRoute, bulkDeletedRoute, draftRoute, publishedRoutes, publishedRoutesErrors } = useSelector((state: AppState) => state.routePlanning);
  const { error: usersError, resetPwdSuccess } = useSelector((state: AppState) => state.users);
  const { countryCode, userFeatureConfigDetails } = useSelector((state: AppState) => state.common);
 
  const dispatch = useDispatch(); 
  const hasHeader = isLoggedIn;

  const [openSideMenuOnClick, setOpenSideMenuOnClick] = useState(false);
  const uacPermissions = useMemo(() => uacData?.uac ?? [], [uacData]);

  const handleSideMenuOpen = (openState: boolean) => {
    setOpenSideMenuOnClick(openState);
  }

  useEffect(() => {
    const unlisten = history.listen((route: any) => {
      if (AppConstants.ROUTES.HOME === route.pathname) {
        dispatch(setLoggedInUser());
      }
      if (userLoginError) {
        dispatch(resetUserError());
      }
      if (planningOrderListError) {
        dispatch(resetPlanningError());
      }
      if (usersError) {
        dispatch(resetUsersError());
      }
      if (routeCreateSuccess) {
        dispatch(resetCreateRouteSuccess());
      }
      if (draftRoute.route) {
        dispatch(resetDraftRoute());
      }
      if (resetPwdSuccess) {
        dispatch(resetUsersResetPwd());
      }
      if (publishedRoutes.length) {
        dispatch(clearPublishedRoutes());
      }
      if (publishedRoutesErrors.length) {
        dispatch(clearPublishedRoutesErrors());
      }
      if (route.pathname !== AppConstants.ROUTES.PLANNING) {
        if (updatedRoute) {
          dispatch(clearUpdatedRoute());
        }
        if (deletedRoute) {
          dispatch(clearDeletedRoute());
        }
        if (bulkDeletedRoute) {
          dispatch(clearBulkDeletedRoute());
        }
      }
    });
    return () => {
      unlisten();
    }
  }, [history, planningOrderListError, userLoginError, usersError, updatedRoute, deletedRoute, bulkDeletedRoute, routeCreateSuccess, resetPwdSuccess, publishedRoutes, draftRoute, publishedRoutesErrors, dispatch]);

  // Load UAC permissions when user is logged in
  useEffect(() => {
    if (isLoggedIn && !uacData) {
      dispatch(fetchUserAccessControl());
    }
  }, [isLoggedIn, uacData, dispatch]);


  // Block only while the first access-control fetch is still in flight (or hasn't
  // started yet). Once it settles - whether with fresh data, a cached fallback
  // (see fetchUserAccessControl), or a terminal failure - fall through and render
  // the app shell instead of blocking the whole screen indefinitely.
  if (isLoggedIn && !uacData && !uacError) {
    return <div className="uac-loading" />;
  }

  return (
    <UACProvider uacPermissions={uacPermissions}>
      {hasHeader && <Header openSideMenuOnClick={openSideMenuOnClick} handleDrawerOnClick={handleSideMenuOpen} />}
      <main className={`routesContainer ${hasHeader ? "hasHeader" : ""} ${openSideMenuOnClick ? "open" : ""}`}>
        {isLoggedIn && uacError && !uacData && (
          <div className="uac-error-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '12px', background: '#fff3cd', color: '#664d03' }}>
            <span>{AppConstants.UAC_FETCH_FAILED_MSG}</span>
            <button onClick={() => dispatch(fetchUserAccessControl())} style={{ padding: '4px 12px', cursor: 'pointer' }}>Retry</button>
          </div>
        )}
        <Routes
          role={role}
          isLoggedIn={isLoggedIn}
          countryCode={countryCode}
          features={userFeatureConfigDetails}
          uacPermissions={uacPermissions}
          defaultMenu={uacData?.defaultMenu || undefined}
        />
      </main>
    </UACProvider>
  );
};

export default withRouter(App);

